import { Card, CardHeader, CardContent } from '@mui/material';
import { ReactNode, useMemo } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  XAxis,
  YAxis,
  Legend,
  Bar,
  Tooltip,
  TooltipContentProps,
  LabelList,
} from 'recharts';
import { Props as LegendContentProps } from 'recharts/types/component/DefaultLegendContent';
import {
  GroupedDataItem,
  GroupedDataItemDefinitionsMap,
  GroupedDataItemOrderArray,
  GroupedDataItemKey,
  YearlyData,
} from './types';
import { useLanguage } from '../../../contexts/language';
import { Props as LabelProps } from 'recharts/types/component/Label';

const ensureTwoDecimals = (value: unknown): ReactNode =>
  typeof value === 'number'
    ? Math.round(value * 100) / 100
    : (value as ReactNode);

const CustomFieldListItem = ({
  label,
  value,
  color,
}: {
  label: string;
  value?: number;
  color?: string;
}) => {
  const hasValue = value != null;
  return (
    <li className="whitespace-nowrap flex gap-1 items-center">
      {color && (
        <span
          className="inline-block rounded-[100%] h-[8px] w-[8px] shadow-inner"
          style={{ backgroundColor: color }}
        />
      )}
      <span
        className={color ? undefined : 'font-bold'}
      >{`${label}${hasValue ? ':' : ''}`}</span>
      {hasValue && <span>{ensureTwoDecimals(value)}</span>}
    </li>
  );
};

const CustomLegendContent = <T extends GroupedDataItem>({
  payload,
  fieldDefinitions,
  fieldsOrder,
  yearlyData,
}: LegendContentProps & {
  fieldDefinitions: GroupedDataItemDefinitionsMap<T>;
  fieldsOrder: GroupedDataItemOrderArray<T>;
  yearlyData: YearlyData<T>;
}) => {
  const { t } = useLanguage();
  const total = yearlyData.total;
  let totalValue = 0;
  const listElementsMap: Partial<Record<GroupedDataItemKey<T>, ReactNode>> = {};

  if (payload) {
    payload.forEach(({ value: fieldKey }) => {
      const { color, label } =
        fieldDefinitions[fieldKey as GroupedDataItemKey<T>];
      const value = total[fieldKey as GroupedDataItemKey<T>] as number;

      listElementsMap[fieldKey as GroupedDataItemKey<T>] = (
        <CustomFieldListItem
          key={fieldKey as string}
          label={label}
          color={color}
          value={value}
        />
      );

      totalValue += value;
    });
  }
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 justify-center">
      {fieldsOrder.length > 1 &&
        fieldsOrder.map(fieldKey => listElementsMap[fieldKey])}
      <CustomFieldListItem label={t('content.total')} value={totalValue} />
    </ul>
  );
};

const CustomTooltipContent = <T extends GroupedDataItem>({
  fieldDefinitions,
  fieldsOrder,
  monthlyTotals,
  label: month,
  labelFormatter,
  payload,
}: TooltipContentProps<number, string> & {
  fieldDefinitions: GroupedDataItemDefinitionsMap<T>;
  fieldsOrder: GroupedDataItemOrderArray<T>;
  monthlyTotals: Record<string, number>;
}) => {
  const { t } = useLanguage();
  const totalValue = monthlyTotals[month as string] ?? 0;
  const listElementsMap: Partial<Record<GroupedDataItemKey<T>, ReactNode>> = {};

  payload.forEach(({ name: fieldKey, value }) => {
    const { color, label } =
      fieldDefinitions[fieldKey as GroupedDataItemKey<T>];

    listElementsMap[fieldKey as GroupedDataItemKey<T>] = (
      <CustomFieldListItem
        key={fieldKey as string}
        label={label}
        color={color}
        value={value as number}
      />
    );
  });

  return (
    <Card variant="outlined" className="p-2">
      <header>{labelFormatter?.(month as string, payload)}</header>
      <ul>
        {fieldsOrder.length > 1 &&
          fieldsOrder.map(fieldKey => listElementsMap[fieldKey])}
        <CustomFieldListItem label={t('content.total')} value={totalValue} />
      </ul>
    </Card>
  );
};

const FONT_HEIGHT = 12;
const TITLE_BOTTOM_SPACING = 8;

const renderBarLabel = ({
  value,
  width,
  offset,
  height,
  ...otherProps
}: LabelProps): ReactNode => {
  if (!value) {
    return null;
  }

  const x = Number(otherProps.x ?? 0),
    y = Number(otherProps.y ?? 0);

  return (
    <text
      offset={offset}
      width={width}
      height={height}
      x={Number(x) + Number(width) / 2}
      y={Number(y) - TITLE_BOTTOM_SPACING}
      fontSize={FONT_HEIGHT}
      className="fill-black font-medium"
      textAnchor="middle"
    >
      {ensureTwoDecimals(value)}
    </text>
  );
};

export interface AnalyticsCardProps<T extends GroupedDataItem> {
  fullWidth?: boolean;
  headerActions?: ReactNode;
  title: string;
  yearlyData: YearlyData<T>;
  fieldDefinitions: GroupedDataItemDefinitionsMap<T>;
  fieldsOrder: GroupedDataItemOrderArray<T>;
  monthFormatter: (month: string, reduced?: boolean) => string;
}

const AnalyticsCard = <T extends GroupedDataItem>({
  fullWidth,
  title,
  fieldDefinitions,
  fieldsOrder,
  headerActions,
  yearlyData,
  monthFormatter,
}: AnalyticsCardProps<T>) => {
  const { monthlyTotals, orderedBars } = useMemo(() => {
    // The monthly totals are calculated to be displayed on each bar
    const monthlyTotals: Record<string, number> = {};
    yearlyData.grouped.forEach(groupedDataItem => {
      let monthlyTotal = 0;
      fieldsOrder.forEach(fieldKey => {
        monthlyTotal += groupedDataItem[fieldKey] as number;
      });
      monthlyTotals[groupedDataItem.month] = monthlyTotal;
    });

    // For bars to be stacked from top to down (first element at the top) they have to be rendered in reverse order
    const orderedBars = [...fieldsOrder].reverse();
    return { monthlyTotals, orderedBars };
  }, [yearlyData, fieldsOrder]);

  return (
    <Card
      variant="outlined"
      className={`h-[362px] ${fullWidth ? 'col-span-full' : 'cols-span-1'} grid grid-rows-[min-content_1fr]`}
    >
      <CardHeader
        className="grid grid-cols-[1fr_min-content]"
        title={title}
        action={headerActions}
      />
      <CardContent>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            reverseStackOrder
            data={yearlyData.grouped}
            margin={{ top: 32 }}
            barSize={24}
          >
            <XAxis
              dataKey="month"
              tickFormatter={(month: string) => monthFormatter(month, true)}
            />
            <YAxis allowDecimals={false} />
            <Tooltip<number, string>
              labelFormatter={(month: string) => monthFormatter(month)}
              content={props => (
                <CustomTooltipContent<T>
                  {...props}
                  fieldsOrder={fieldsOrder}
                  monthlyTotals={monthlyTotals}
                  fieldDefinitions={fieldDefinitions}
                />
              )}
            />
            <Legend
              verticalAlign="top"
              wrapperStyle={{ transform: 'translateY(-32px)' }}
              content={props => (
                <CustomLegendContent
                  {...props}
                  fieldsOrder={fieldsOrder}
                  fieldDefinitions={fieldDefinitions}
                  yearlyData={yearlyData}
                />
              )}
            />
            {orderedBars.map((fieldKey, index) => {
              const { color } = fieldDefinitions[fieldKey];
              return (
                <Bar
                  key={fieldKey as string}
                  dataKey={fieldKey as string}
                  stackId="a"
                  fill={color}
                >
                  {
                    // The label with the total of the month is only displayed in the last segment (the one at the top)
                    index === orderedBars.length - 1 && (
                      <LabelList content={renderBarLabel}></LabelList>
                    )
                  }
                </Bar>
              );
            })}
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default AnalyticsCard;
